import React, { useEffect, useState } from 'react';
import { Bell, BellRing, X } from 'lucide-react';
import { pushSupported, getSubscription, subscribeToPush } from '../../utils/push';

const DISMISSED_KEY = 'outlaw_push_prompt_dismissed_v1';
const FIRST_VISIT_KEY = 'outlaw_first_visit_ts_v1';
const MIN_AGE_MS = 60 * 60 * 1000; // 60 minutes since first visit
const POLL_MS = 30 * 1000;         // re-check every 30s while page open

const NotificationsPrompt = () => {
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!pushSupported()) return undefined;
    if (typeof Notification !== 'undefined' && Notification.permission === 'denied') return undefined;
    let cancelled = false;

    // Record the first-visit timestamp on the very first pageview.
    let firstVisit;
    try {
      firstVisit = Number(localStorage.getItem(FIRST_VISIT_KEY));
      if (!firstVisit || Number.isNaN(firstVisit)) {
        firstVisit = Date.now();
        localStorage.setItem(FIRST_VISIT_KEY, String(firstVisit));
      }
    } catch {
      firstVisit = Date.now();
    }

    const maybeShow = async () => {
      if (cancelled) return;
      // Honour permanent dismiss.
      try { if (localStorage.getItem(DISMISSED_KEY)) return; } catch { /* ignore */ }
      // Not before 60 minutes since first visit.
      if (Date.now() - firstVisit < MIN_AGE_MS) return;
      // Already subscribed? Nothing to prompt.
      try {
        const sub = await getSubscription();
        if (sub) return;
      } catch { /* ignore */ }
      if (!cancelled) setVisible(true);
    };

    maybeShow();
    const timer = setInterval(maybeShow, POLL_MS);
    return () => { cancelled = true; clearInterval(timer); };
  }, []);

  const dismiss = () => {
    setVisible(false);
    try {
      localStorage.setItem(DISMISSED_KEY, '1');
    } catch {
      /* ignore */
    }
  };

  const enable = async () => {
    setBusy(true);
    setError('');
    try {
      await subscribeToPush();
      dismiss();
    } catch (e) {
      setError(e.message || 'Could not enable notifications.');
    } finally {
      setBusy(false);
    }
  };

  if (!visible) return null;

  return (
    <div
      data-testid="notif-prompt"
      className="fixed left-3 right-3 bottom-3 sm:right-6 sm:left-auto sm:bottom-6 sm:max-w-sm z-40 bg-zinc-950/95 border border-zinc-800 rounded-lg p-4"
    >
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss"
        data-testid="notif-prompt-dismiss"
        className="absolute top-2 right-2 w-7 h-7 rounded-full text-gray-500 hover:text-white hover:bg-zinc-800 flex items-center justify-center"
      >
        <X className="h-4 w-4" />
      </button>

      <div className="flex items-start gap-3 mb-3 pr-6">
        <div className="w-11 h-11 rounded-xl bg-red-600/20 border border-red-500/40 flex items-center justify-center shrink-0 shadow-[0_0_18px_rgba(220,38,38,0.45)]">
          <BellRing className="h-5 w-5 text-red-400" />
        </div>
        <div className="min-w-0">
          <p className="text-[10px] font-black tracking-[0.25em] text-red-400 uppercase mb-1">
            Never miss a drop
          </p>
          <p className="text-base font-black text-white leading-tight">
            Push alerts for breaking stories
          </p>
          <p className="text-xs text-gray-400 leading-snug mt-1">
            One ping the moment we break something raw. No spam, no marketing.
          </p>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={enable}
          disabled={busy}
          data-testid="notif-prompt-enable"
          className="flex-1 bg-red-600 hover:bg-red-500 disabled:opacity-60 text-white font-black tracking-widest uppercase text-xs px-4 py-2.5 rounded-lg shadow-[0_0_18px_rgba(220,38,38,0.55)] transition-all inline-flex items-center justify-center gap-2"
        >
          <Bell className="h-4 w-4" />
          {busy ? 'Enabling…' : 'Enable Alerts'}
        </button>
        <button
          type="button"
          onClick={dismiss}
          data-testid="notif-prompt-later"
          className="text-[10px] font-bold tracking-widest uppercase text-gray-500 hover:text-white px-2"
        >
          Not now
        </button>
      </div>
      {error && (
        <p className="text-xs text-red-400 mt-2" data-testid="notif-prompt-error">
          {error}
        </p>
      )}
    </div>
  );
};

export default NotificationsPrompt;
